import dayjs, { Dayjs } from "dayjs";
import { StripeOrderStatus } from "../../Stripe/StripeOrderDTO.ts";
import { BonificoOrderStatus } from "../../ProductService/BonificoOrder/BonificoOrderDTO.ts";
import {
  paidAmountOf,
  refundedAmountOf,
  SaleKind,
  SaleOutcome,
  SaleRecord,
  SALES_GATEWAYS,
  SalesGateway,
  sumOf,
} from "./SaleRecord.ts";
import { isWithin, SalesPeriod } from "./salesPeriod.ts";
import { ordersPlacedIn, salesConcludedIn } from "./salesSummary.ts";
import { itemKeyOf } from "./salesFilters.ts";

export const EXPECTED_COLLECTION_MONTHS = 12;

const MILLISECONDS_IN_DAY = 24 * 60 * 60 * 1000;

const STATUSES_OF_RUNNING_PLANS: string[] = [
  StripeOrderStatus.InstallmentPaid,
  StripeOrderStatus.InstallmentDelinquent,
];

const BONIFICO_STATUSES_AWAITING_TRANSFER: string[] = [
  BonificoOrderStatus.PendingPayment,
  BonificoOrderStatus.PartiallyPaid,
];

export type ItemSales = {
  itemKey: string;
  orderCount: number;
  orderCountByGateway: Partial<Record<SalesGateway, number>>;
  soldInCents: number;
  shareOfSold: number;
};

export function salesByItem(
  records: SaleRecord[],
  period: SalesPeriod,
): ItemSales[] {
  const concludedSales = salesConcludedIn(records, period);
  const soldInCents = sumOf(concludedSales, (sale) => sale.amountDueInCents);
  const salesByKey = new Map<string, SaleRecord[]>();
  for (const sale of concludedSales) {
    const itemKey = itemKeyOf(sale);
    if (itemKey)
      salesByKey.set(itemKey, [...(salesByKey.get(itemKey) ?? []), sale]);
  }
  return [...salesByKey.entries()]
    .map(([itemKey, sales]) => {
      const itemSoldInCents = sumOf(sales, (sale) => sale.amountDueInCents);
      return {
        itemKey,
        orderCount: sales.length,
        orderCountByGateway: Object.fromEntries(
          countBy(sales.map((sale) => sale.gateway)),
        ),
        soldInCents: itemSoldInCents,
        shareOfSold: soldInCents > 0 ? itemSoldInCents / soldInCents : 0,
      };
    })
    .sort((first, second) => second.soldInCents - first.soldInCents);
}

export type InstallmentsHealth = {
  runningPlanCount: number;
  delinquentPlanCount: number;
  delinquencyRate: number;
  overdueInCents: number;
  upcomingInCents: number;
  cancelledPlanCount: number;
  unpaidOnCancelledPlansInCents: number;
};

function stripeInstallmentPlansOf(records: SaleRecord[]): SaleRecord[] {
  return records.filter(
    (record) =>
      record.gateway === SalesGateway.Stripe &&
      (record.numberOfInstallments ?? 1) > 1,
  );
}

function runningPlansOf(records: SaleRecord[]): SaleRecord[] {
  return stripeInstallmentPlansOf(records).filter((plan) =>
    STATUSES_OF_RUNNING_PLANS.includes(plan.status),
  );
}

export function installmentsHealthOf(
  records: SaleRecord[],
  period: SalesPeriod,
): InstallmentsHealth {
  const stripePlans = stripeInstallmentPlansOf(records);
  const runningPlans = runningPlansOf(records);
  const delinquentPlans = runningPlans.filter(
    (plan) => plan.status === StripeOrderStatus.InstallmentDelinquent,
  );
  const cancelledPlans = salesConcludedIn(stripePlans, period).filter(
    (plan) => plan.status === StripeOrderStatus.SubscriptionCancelled,
  );
  const upcomingInstallments = runningPlans.flatMap(
    (plan) => plan.upcomingInstallments,
  );
  return {
    runningPlanCount: runningPlans.length,
    delinquentPlanCount: delinquentPlans.length,
    delinquencyRate:
      runningPlans.length > 0
        ? delinquentPlans.length / runningPlans.length
        : 0,
    overdueInCents: sumOf(runningPlans, (plan) => plan.overdueInCents),
    upcomingInCents: sumOf(
      upcomingInstallments,
      (installment) => installment.amountInCents,
    ),
    cancelledPlanCount: cancelledPlans.length,
    unpaidOnCancelledPlansInCents: sumOf(cancelledPlans, (plan) =>
      Math.max(0, plan.amountDueInCents - paidAmountOf(plan)),
    ),
  };
}

export type ExpectedCollectionsOfMonth = {
  month: Dayjs;
  stripeInstallmentsInCents: number;
  awaitingTransfersInCents: number;
};

export function expectedCollectionsOf(
  records: SaleRecord[],
  today: Dayjs,
): ExpectedCollectionsOfMonth[] {
  const upcomingInstallments = runningPlansOf(records).flatMap(
    (plan) => plan.upcomingInstallments,
  );
  const awaitingTransfersInCents = sumOf(
    bonificoOrdersAwaitingTransfer(records),
    amountStillToPayOf,
  );
  const currentMonth = today.startOf("month");
  return Array.from({ length: EXPECTED_COLLECTION_MONTHS }, (_, index) => {
    const month = currentMonth.add(index, "month");
    return {
      month,
      stripeInstallmentsInCents: sumOf(
        upcomingInstallments.filter((installment) =>
          dayjs(installment.dueAtUtc).isSame(month, "month"),
        ),
        (installment) => installment.amountInCents,
      ),
      awaitingTransfersInCents: index === 0 ? awaitingTransfersInCents : 0,
    };
  });
}

export type DeclineReasonCount = {
  declineReason: string | null;
  count: number;
};

export type GatewayOutcomes = {
  gateway: SalesGateway;
  orderCount: number;
  countByOutcome: Record<SaleOutcome, number>;
  completionRate: number;
  declineReasons: DeclineReasonCount[];
};

function countBy<Key>(keys: Key[]): Map<Key, number> {
  const counts = new Map<Key, number>();
  for (const key of keys) counts.set(key, (counts.get(key) ?? 0) + 1);
  return counts;
}

export function outcomesByGateway(
  records: SaleRecord[],
  period: SalesPeriod,
): GatewayOutcomes[] {
  const ordersOfThePeriod = ordersPlacedIn(records, period);
  return SALES_GATEWAYS.map((gateway) => {
    const orders = ordersOfThePeriod.filter(
      (order) => order.gateway === gateway,
    );
    const outcomeCounts = countBy(orders.map((order) => order.outcome));
    const countByOutcome = Object.fromEntries(
      Object.values(SaleOutcome).map((outcome) => [
        outcome,
        outcomeCounts.get(outcome) ?? 0,
      ]),
    ) as Record<SaleOutcome, number>;
    return {
      gateway,
      orderCount: orders.length,
      countByOutcome,
      completionRate:
        orders.length > 0
          ? countByOutcome[SaleOutcome.Completed] / orders.length
          : 0,
      declineReasons: [
        ...countBy(
          orders
            .filter((order) => order.outcome === SaleOutcome.Declined)
            .map((order) => order.declineReason),
        ).entries(),
      ]
        .map(([declineReason, count]) => ({ declineReason, count }))
        .sort((first, second) => second.count - first.count),
    };
  }).filter((outcomes) => outcomes.orderCount > 0);
}

function bonificoOrdersAwaitingTransfer(records: SaleRecord[]): SaleRecord[] {
  return records.filter(
    (record) =>
      record.gateway === SalesGateway.Bonifico &&
      BONIFICO_STATUSES_AWAITING_TRANSFER.includes(record.status),
  );
}

function amountStillToPayOf(order: SaleRecord): number {
  return Math.max(0, order.amountDueInCents - paidAmountOf(order));
}

export type BonificoPayments = {
  paidOrderCount: number;
  averageDaysToPay: number | null;
  awaitingTransferCount: number;
  awaitingTransferInCents: number;
  draftCount: number;
  draftsInCents: number;
};

export function bonificoPaymentsOf(
  records: SaleRecord[],
  period: SalesPeriod,
): BonificoPayments {
  const bonificoSales = records.filter(
    (record) =>
      record.gateway === SalesGateway.Bonifico && record.kind === SaleKind.Sale,
  );
  const daysToPay = bonificoSales.flatMap((sale) => {
    const firstPayment = sale.payments[0];
    if (!firstPayment || !isWithin(firstPayment.paidAtUtc, period)) return [];
    return [
      Math.max(
        0,
        (dayjs(firstPayment.paidAtUtc).valueOf() -
          dayjs(sale.orderedAtUtc).valueOf()) /
          MILLISECONDS_IN_DAY,
      ),
    ];
  });
  const awaitingTransfer = bonificoOrdersAwaitingTransfer(records);
  const drafts = bonificoSales.filter(
    (sale) => sale.status === BonificoOrderStatus.Draft,
  );
  return {
    paidOrderCount: daysToPay.length,
    averageDaysToPay:
      daysToPay.length > 0
        ? sumOf(daysToPay, (days) => days) / daysToPay.length
        : null,
    awaitingTransferCount: awaitingTransfer.length,
    awaitingTransferInCents: sumOf(awaitingTransfer, amountStillToPayOf),
    draftCount: drafts.length,
    draftsInCents: sumOf(drafts, (draft) => draft.amountDueInCents),
  };
}

export type CouponSales = {
  couponCode: string;
  orderCount: number;
  discountInCents: number;
  soldInCents: number;
};

function hasCoupon(sale: SaleRecord): boolean {
  return sale.couponCode !== null;
}

export function salesWithCouponIn(
  records: SaleRecord[],
  period: SalesPeriod,
): SaleRecord[] {
  return salesConcludedIn(records, period).filter(hasCoupon);
}

export type CouponUsage = {
  ordersWithCouponCount: number;
  shareOfOrders: number;
  discountInCents: number;
  coupons: CouponSales[];
};

export function couponUsageOf(
  records: SaleRecord[],
  period: SalesPeriod,
): CouponUsage {
  const concludedSales = salesConcludedIn(records, period);
  const salesWithCoupon = concludedSales.filter(hasCoupon);
  const salesByCoupon = new Map<string, SaleRecord[]>();
  for (const sale of salesWithCoupon) {
    const couponCode = sale.couponCode as string;
    salesByCoupon.set(couponCode, [
      ...(salesByCoupon.get(couponCode) ?? []),
      sale,
    ]);
  }
  return {
    ordersWithCouponCount: salesWithCoupon.length,
    shareOfOrders:
      concludedSales.length > 0
        ? salesWithCoupon.length / concludedSales.length
        : 0,
    discountInCents: sumOf(
      salesWithCoupon,
      (sale) => sale.couponDiscountInCents,
    ),
    coupons: [...salesByCoupon.entries()]
      .map(([couponCode, sales]) => ({
        couponCode,
        orderCount: sales.length,
        discountInCents: sumOf(sales, (sale) => sale.couponDiscountInCents),
        soldInCents: sumOf(sales, (sale) => sale.amountDueInCents),
      }))
      .sort(
        (first, second) =>
          second.orderCount - first.orderCount ||
          second.discountInCents - first.discountInCents,
      ),
  };
}

function isRefunded(sale: SaleRecord): boolean {
  return refundedAmountOf(sale) > 0;
}

export function refundedSalesIn(
  records: SaleRecord[],
  period: SalesPeriod,
): SaleRecord[] {
  return salesConcludedIn(records, period).filter(isRefunded);
}

export type ItemRefunds = {
  itemKey: string;
  soldInCents: number;
  refundedInCents: number;
  refundedOrderCount: number;
  refundedOrderCountByGateway: Partial<Record<SalesGateway, number>>;
  refundRate: number;
};

export function refundsByItem(
  records: SaleRecord[],
  period: SalesPeriod,
): ItemRefunds[] {
  const salesByKey = new Map<string, SaleRecord[]>();
  for (const sale of salesConcludedIn(records, period)) {
    const itemKey = itemKeyOf(sale);
    if (itemKey)
      salesByKey.set(itemKey, [...(salesByKey.get(itemKey) ?? []), sale]);
  }
  return [...salesByKey.entries()]
    .map(([itemKey, sales]) => {
      const soldInCents = sumOf(sales, (sale) => sale.amountDueInCents);
      const refundedInCents = sumOf(sales, refundedAmountOf);
      const refundedSales = sales.filter(isRefunded);
      return {
        itemKey,
        soldInCents,
        refundedInCents,
        refundedOrderCount: refundedSales.length,
        refundedOrderCountByGateway: Object.fromEntries(
          countBy(refundedSales.map((sale) => sale.gateway)),
        ),
        refundRate: soldInCents > 0 ? refundedInCents / soldInCents : 0,
      };
    })
    .filter((item) => item.refundedInCents > 0)
    .sort((first, second) => second.refundedInCents - first.refundedInCents);
}

export const WEEKDAYS_FROM_MONDAY = [1, 2, 3, 4, 5, 6, 0];
export const HOURS_OF_THE_DAY = Array.from({ length: 24 }, (_, hour) => hour);

export type SalesHeatmapCell = {
  weekday: number;
  hour: number;
  orderCount: number;
  soldInCents: number;
};

export function salesHeatmapOf(
  records: SaleRecord[],
  period: SalesPeriod,
): SalesHeatmapCell[] {
  const cells = WEEKDAYS_FROM_MONDAY.flatMap((weekday) =>
    HOURS_OF_THE_DAY.map((hour) => ({
      weekday,
      hour,
      orderCount: 0,
      soldInCents: 0,
    })),
  );
  for (const sale of salesConcludedIn(records, period)) {
    const orderedAt = dayjs(sale.orderedAtUtc);
    const cell =
      cells[
        WEEKDAYS_FROM_MONDAY.indexOf(orderedAt.day()) *
          HOURS_OF_THE_DAY.length +
          orderedAt.hour()
      ];
    cell.orderCount += 1;
    cell.soldInCents += sale.amountDueInCents;
  }
  return cells;
}
